import type { CustomerDevice } from '@/services/db/customer/entity';
import { sortDevices } from '@/services/db/customer/utils';
import type { Delivery } from '@/services/db/delivery/entity';
import type { PushDeliveryMetaReason } from '@/services/db/delivery/utils';

/**
 * Groups a delivery's per-device push statuses into the buckets the results
 * accordion renders, ported from the Ember `push-action-results` component's
 * `deliveryGroups` getter. A device is `sent` when its `status` is 200;
 * `undeliverable` / `bounced` / `suppressed` come off their boolean flags; and
 * everything not already in one of those four buckets is `failed` (Ember's
 * "not already categorized" set difference). Empty buckets are dropped.
 *
 * A drafted push shows a single `drafted` bucket of its target devices instead
 * (Ember's `recipientDevices` branch) — see `draftedRecipientDevices`.
 */

const SUCCESS_STATUS = 200;

export type PushDeliveryGroupType = 'drafted' | 'sent' | 'undeliverable' | 'bounced' | 'suppressed' | 'failed';

export interface PushDeliveryGroup {
  type: PushDeliveryGroupType;
  devices: PushDeliveryMetaReason[];
}

function sentDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  return devices.filter((device) => device.status === SUCCESS_STATUS);
}

function undeliverableDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  return devices.filter((device) => device.undeliverable === true);
}

function bouncedDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  return devices.filter((device) => device.bounced === true);
}

function suppressedDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  return devices.filter((device) => device.suppressed === true);
}

/** Everything not sent / undeliverable / bounced / suppressed (Ember's set difference). */
function failedDevices(devices: PushDeliveryMetaReason[]): PushDeliveryMetaReason[] {
  const categorized = new Set<PushDeliveryMetaReason>([
    ...sentDevices(devices),
    ...undeliverableDevices(devices),
    ...bouncedDevices(devices),
    ...suppressedDevices(devices),
  ]);
  return devices.filter((device) => !categorized.has(device));
}

export function groupDeviceStatuses(devices: PushDeliveryMetaReason[]): PushDeliveryGroup[] {
  const groups: PushDeliveryGroup[] = [
    { type: 'sent', devices: sentDevices(devices) },
    { type: 'undeliverable', devices: undeliverableDevices(devices) },
    { type: 'bounced', devices: bouncedDevices(devices) },
    { type: 'suppressed', devices: suppressedDevices(devices) },
    { type: 'failed', devices: failedDevices(devices) },
  ];
  return groups.filter((group) => group.devices.length > 0);
}

/**
 * A drafted push delivery's target devices, shaped as the results-table rows
 * (Ember `deliveryGroups` drafted branch mapping `recipientDevices` to
 * `status: 0`, empty detail). The customer's devices are sorted `last_used`
 * desc and filtered by the delivery's `recipient` (Ember `recipientDevices`).
 */
export function draftedRecipientDevices(delivery: Delivery, devices: CustomerDevice[]): PushDeliveryMetaReason[] {
  return recipientDevices(delivery.recipient, devices).map((device) => ({
    id: device.id,
    platform: device.platform ?? '',
    status: 0,
    detail: '',
    bounced: false,
    suppressed: false,
  }));
}

/** The customer's devices (`last_used` desc) targeted by the delivery's `recipient` (Ember `recipientDevices`). */
function recipientDevices(recipient: string | null | undefined, devices: CustomerDevice[]): CustomerDevice[] {
  const sorted = sortDevices(devices);
  if (recipient === 'last_used') return sorted.slice(0, 1);
  if (recipient === 'all') return sorted;
  return extractTokens(recipient)
    .map((token) => sorted.find((device) => device.id === token))
    .filter((device): device is CustomerDevice => device != null);
}

/**
 * The device tokens a non-`last_used`/`all` recipient targets (Ember `extractTokens`):
 * a JSON array of ids, or — when the value isn't valid JSON — the raw single token.
 * Valid JSON that isn't an array (e.g. a bare number) targets nothing.
 */
function extractTokens(recipient: string | null | undefined): string[] {
  if (recipient == null) return [];
  try {
    const parsed: unknown = JSON.parse(recipient);
    return Array.isArray(parsed) ? parsed.filter((token): token is string => typeof token === 'string') : [];
  } catch {
    return [recipient];
  }
}
